import { Button, Modal, Tag, Space } from "antd";

import { useApolloDrive } from "../../hooks/useApolloDrive";
import { CommandStatus } from "../../hooks/useApolloCommand";

/**
 * 주행 제어 패널(설계 §10, §11.2, Stage 3) — 자율주행 개시/정지. 실차를 움직이므로 안전 요구가 구현의 일부.
 * - 2단계 분리: 경로 전송(RouteEditPanel)과 별개 버튼(§10.3)
 * - 개시 선행조건 게이트: engageAdvice === READY_TO_ENGAGE 아니면 비활성 + 사유 표시(§10.4)
 * - 개시 확인 모달: 차량번호 표시(다른 차 오출발 방지, §10.3)
 * - 정지는 상시 가능하나 보조 수단(물리 E-STOP 이 1차 안전장치, §10.1)
 * - 개시는 ADMIN 전용(R3), 정지는 ADMIN/MANAGER
 */

const STATUS_LABEL: { [k in CommandStatus | "idle"]: string } = {
    idle: "-",
    editing: "-",
    PENDING: "전달 대기",
    SENT: "차량 수신",
    ACK: "실행됨",
    FAILED: "실패",
    EXPIRED: "만료(미전달)",
};

export default function DriveControlPanel({
    carId,
    carNumber,
    engageAdvice,
    isAdmin,
}: {
    carId: number | null;
    carNumber: string | null;
    engageAdvice: string | undefined;
    isAdmin: boolean;
}) {
    const drive = useApolloDrive();

    const ready = engageAdvice === "READY_TO_ENGAGE";
    // KEEP_ENGAGED/PREPARE_DISENGAGE = 이미 자율주행(engaged) 상태 → 개시 불필요(Dreamview 동일).
    const engaged = engageAdvice === "KEEP_ENGAGED" || engageAdvice === "PREPARE_DISENGAGE";
    const canStart = isAdmin && carId != null && ready && !drive.sending;
    const canStop = carId != null && !drive.sending;

    // engageAdvice(EngageAdvice.Advice) → 화면 상태
    const engageMeta = ready
        ? { label: "개시 가능", color: "success" }
        : engageAdvice === "KEEP_ENGAGED"
        ? { label: "자율주행 중", color: "processing" }
        : engageAdvice === "PREPARE_DISENGAGE"
        ? { label: "해제 준비 중", color: "warning" }
        : { label: engageAdvice || "대기(모듈 확인)", color: "warning" };

    const confirmStart = () => {
        if (carId == null) return;
        Modal.confirm({
            title: "자율주행 개시",
            centered: true,
            okText: "개시",
            okButtonProps: { danger: true },
            cancelText: "취소",
            content: (
                <div style={{ fontSize: 13, lineHeight: 1.7 }}>
                    <div>
                        차량 <b style={{ color: "#c0392b" }}>{carNumber || carId}</b> 을(를) 자율주행으로 개시합니다.
                    </div>
                    <div style={{ color: "#8a5a00", marginTop: 6 }}>
                        ⚠ 실차가 실제로 움직입니다. 차량 주변 안전을 확인하세요.
                    </div>
                </div>
            ),
            onOk() {
                drive.send(carId, "start");
            },
        });
    };

    return (
        <div
            style={{
                position: "absolute",
                bottom: 12,
                right: 12,
                zIndex: 12,
                width: 268,
                background: "rgba(20,10,10,0.92)",
                border: "1px solid #4a2a2a",
                borderRadius: 8,
                padding: 12,
                color: "#f0e0e0",
            }}
        >
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                <span>⚠ 주행 제어</span>
                <Tag color="red" style={{ marginLeft: "auto" }}>ADMIN</Tag>
            </div>

            {/* 개시 게이트 상태 */}
            <div style={{ fontSize: 12, marginBottom: 8 }}>
                <span style={{ color: "#c9a9a9" }}>상태: </span>
                <Tag color={engageMeta.color}>{engageMeta.label}</Tag>
            </div>

            <Space direction="vertical" style={{ width: "100%" }} size={8}>
                <Button
                    type="primary"
                    danger
                    block
                    disabled={!canStart}
                    onClick={confirmStart}
                    title={
                        !isAdmin
                            ? "개시는 ADMIN 전용"
                            : engaged
                            ? "이미 자율주행 중입니다(개시 불필요)"
                            : !ready
                            ? "개시 가능(READY_TO_ENGAGE) 상태가 아닙니다"
                            : undefined
                    }
                    style={!canStart ? { background: "#2a1a1a", borderColor: "#4a2a2a", color: "#8a6b6b" } : undefined}
                >
                    자율주행 시작
                </Button>
                <Button
                    block
                    disabled={!canStop}
                    onClick={() => carId != null && drive.send(carId, "stop")}
                    style={{
                        background: canStop ? "#3a2222" : "transparent",
                        borderColor: canStop ? "#6a3838" : "#4a2a2a",
                        color: canStop ? "#f0d8d8" : "#7a5a5a",
                    }}
                >
                    정지 / 수동 전환
                </Button>
            </Space>

            {engaged && (
                <div style={{ marginTop: 8, fontSize: 11.5, color: "#c9b090", lineHeight: 1.5 }}>
                    이미 자율주행 중입니다. 경로 전송만으로 주행하며, 멈추려면 "정지/수동 전환"을 누르세요.
                </div>
            )}

            <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                <span style={{ color: "#c9a9a9" }}>명령</span>
                <span style={{ color: "#f0e0e0" }}>
                    {drive.action === "start" ? "개시" : drive.action === "stop" ? "정지" : "-"}
                </span>
                <Tag>{STATUS_LABEL[drive.status]}</Tag>
            </div>
            {drive.error && <div style={{ marginTop: 6, fontSize: 12, color: "#ff9a9a" }}>{drive.error}</div>}

            <div style={{ marginTop: 10, fontSize: 11, color: "#b08a8a", lineHeight: 1.5 }}>
                ⚠ 이 정지는 보조 수단입니다. 비상시 물리 E-STOP·차량 내 수단을 사용하세요.
            </div>
        </div>
    );
}
